import type { GetApiPartnersData } from '@/api';
import type { TableSort } from '@/ui';

import { formatSort, isSameSort, parseSort } from '../../../lib/tableSort';

/*
 * Состояние списка клиентов в search params маршрута. Клиенты - партнёры 1С с
 * признаком «Клиент»; поставщики и перевозчики в этот экран не попадают.
 */

export const PAGE_SIZE = 20;

export type ActivityFilter = 'active' | 'inactive' | 'all';

/** Поля, по которым бэкенд разрешает сортировать (SearchPartnersQuery.SortFields). */
export const SORT_FIELDS = ['name', 'code'] as const;

const DEFAULT_SORT: TableSort = { field: 'name', dir: 'asc' };
const DEFAULT_ACTIVITY: ActivityFilter = 'active';

export interface CustomersSearch {
  search?: string;
  activity?: ActivityFilter;
  page?: number;
  sort?: string;
}

export interface ResolvedCustomersSearch {
  search: string;
  activity: ActivityFilter;
  page: number;
  sort: TableSort;
}

const isActivityFilter = (value: unknown): value is ActivityFilter =>
  value === 'active' || value === 'inactive' || value === 'all';

export const validateCustomersSearch = (
  raw: Record<string, unknown>,
): CustomersSearch => {
  const result: CustomersSearch = {};

  const search = typeof raw.search === 'string' ? raw.search.trim() : '';
  if (search !== '') result.search = search;

  if (isActivityFilter(raw.activity) && raw.activity !== DEFAULT_ACTIVITY)
    result.activity = raw.activity;

  const page = Number(raw.page);
  if (Number.isInteger(page) && page > 1) result.page = page;

  const sort = parseSort(raw.sort, SORT_FIELDS);
  if (sort && !isSameSort(sort, DEFAULT_SORT)) result.sort = formatSort(sort);

  return result;
};

export const resolveCustomersSearch = (
  search: CustomersSearch,
): ResolvedCustomersSearch => ({
  search: search.search ?? '',
  activity: search.activity ?? DEFAULT_ACTIVITY,
  page: search.page ?? 1,
  sort: parseSort(search.sort, SORT_FIELDS) ?? DEFAULT_SORT,
});

export const toCustomersQuery = (
  resolved: ResolvedCustomersSearch,
): NonNullable<GetApiPartnersData['query']> => {
  const query: NonNullable<GetApiPartnersData['query']> = {
    IsClient: true,
    PageNumber: resolved.page,
    PageSize: PAGE_SIZE,
    Sort: formatSort(resolved.sort),
  };

  if (resolved.search !== '') query.Search = resolved.search;

  // Бэкенд по умолчанию отдаёт действующих - «действующих» не отправляем.
  if (resolved.activity === 'inactive') query.Activity = 'Inactive';
  if (resolved.activity === 'all') query.Activity = 'All';

  return query;
};
